/* global globalThis */
export const MALUKU_API_ROOT = '/maluku/api';
export const MALUKU_PAGE_SIZE = 50;

export function malukuFamilyFilter(surnames) {
    if (!Array.isArray(surnames) || surnames.some(name => typeof name !== 'string')) throw new Error('Invalid tree surnames');
    const names = [...new Set(surnames.map(name => name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '')).filter(Boolean))].sort();
    const value = names.join(',');
    if (names.length > 500 || names.some(name => name.length > 160) || encodeURIComponent(value).length > 6000) throw new Error('Too many tree surnames');
    return value;
}

export function withMalukuFamilyFilter(params, families) {
    if (families !== null && families !== undefined) params.set('families', families);
    return params;
}

// A few research names contain slashes. Give those names a reversible UTF-8
// hex segment so neither Nginx nor FastAPI treats part of the name as a route.
export function malukuNameSegment(name) {
    const value = String(name);
    return value.includes('/') || value.startsWith('~')
        ? `~${Array.from(new TextEncoder().encode(value), byte => byte.toString(16).padStart(2, '0')).join('')}`
        : encodeURIComponent(value);
}

export function malukuNameLabel(segment) {
    if (!segment.startsWith('~')) return segment;
    try {
        if (!/^~(?:[a-f0-9]{2})+$/i.test(segment)) return segment;
        return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(segment.slice(1).match(/../g), hex => parseInt(hex, 16)));
    } catch { return segment; }
}

function malukuTarget(path) {
    const target = new URL(`${MALUKU_API_ROOT}${path}`, 'https://maluku.invalid');
    if (!path.startsWith('/') || path.startsWith('//') || target.origin !== 'https://maluku.invalid'
        || !target.pathname.startsWith(`${MALUKU_API_ROOT}/`) || path.includes('#') || path.includes('\\')
        || /(?:^|\/)(?:\.|%2e){1,2}(?=\/|$)/i.test(path.split('?')[0])) {
        throw new Error('Invalid Maluku endpoint');
    }
    return target;
}

export async function fetchMaluku(path, { signal, fetchImpl = globalThis.fetch } = {}) {
    malukuTarget(path);
    const response = await fetchImpl(`${MALUKU_API_ROOT}${path}`, {
        signal,
        credentials: 'omit',
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
        const error = new Error('Maluku request failed');
        error.status = response.status;
        throw error;
    }
    return response.json();
}

// A selected family's reviewed locations are independent of the camera. Load
// every bounded page once and keep that selection stable while zooming/panning.
export function createMalukuMapRequest() {
    let selected;
    return async (path, options = {}) => {
        const target = malukuTarget(path);
        const key = JSON.stringify(['q', 'island', 'mode', 'families'].map(name => target.searchParams.get(name)));
        if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (selected?.key === key) return selected.data;
        const result = await fetchMaluku(path, options);
        if (!result.family) return result;
        const params = withMalukuFamilyFilter(new URLSearchParams({ name: result.family, island: target.searchParams.get('island') || '' }), target.searchParams.get('families'));
        const collection = createMalukuCollection(`/map/occurrences?${params}`, options);
        let locations;
        do { locations = await collection.loadNext(); } while (!locations.complete);
        const items = target.searchParams.get('mode') === 'families' ? locations.items
            : locations.items.map(item => ({ ...item, kind: 'negeri', name: item.negeri, family_count: 1 }));
        const data = { ...result, items, bounds: locations.bounds, occurrences: locations.occurrences };
        if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        selected = { key, data };
        return data;
    };
}

const collectionCache = new Map();
function waitForPage(delay, signal) {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return new Promise((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, delay);
        signal?.addEventListener('abort', abort, { once: true });
    });
}

// Fetch one bounded page only when requested. Retain already visited rows in
// memory so a panel's back trail can restore its list and scroll position.
export function createMalukuCollection(path, { signal, fetchImpl, pageDelay = 550 } = {}) {
    const target = malukuTarget(path);
    target.searchParams.delete('page'); target.searchParams.delete('per_page');
    const key = target.pathname + target.search;
    const cached = collectionCache.get(key);
    let result = !fetchImpl && cached && cached.expires > Date.now() ? cached.data : null;
    let pending; let lastLoaded = 0;
    target.searchParams.set('per_page', String(MALUKU_PAGE_SIZE));
    async function requestPage() {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (result?.complete) return result;
        const page = (result?.page || 0) + 1;
        if (page > 500) throw new Error('Incomplete Maluku collection');
        if (lastLoaded) await waitForPage(Math.max(0, pageDelay - (Date.now() - lastLoaded)), signal);
        target.searchParams.set('page', String(page));
        const part = await fetchMaluku(target.pathname.slice(MALUKU_API_ROOT.length) + target.search, { signal, fetchImpl });
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (!Array.isArray(part.items) || part.items.length > MALUKU_PAGE_SIZE || !Number.isSafeInteger(part.total)
            || part.total < 0 || part.total > 20000 || (result && part.total !== result.total)
            || (part.total > 0 && !part.items.length)
            || (result?.items.length || 0) + part.items.length > part.total) {
            const error = new Error('Incomplete Maluku collection'); error.status = 503; throw error;
        }
        result = { ...part, page, items: [...(result?.items || []), ...part.items] };
        result.complete = result.items.length === result.total;
        lastLoaded = Date.now();
        if (!fetchImpl) {
            collectionCache.delete(key);
            collectionCache.set(key, { data: result, expires: Date.now() + 60000 });
            if (collectionCache.size > 5) collectionCache.delete(collectionCache.keys().next().value);
        }
        return result;
    }
    return {
        get data() { return result; },
        loadNext() {
            if (!pending) pending = requestPage().finally(() => { pending = null; });
            return pending;
        },
    };
}
